import React, { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import classNames from 'classnames';
import Button from '../../../components/Button';
import ModalDialog from '../../../components/ModalDialog';
import Avatar from '../../../components/Avatar';
import {
  XIcon,
  FilterIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/outline';
import Tag from '../../../svgs/Tag';
import { Combobox, Transition } from '@headlessui/react';
import { getTagStyles, DEFAULT_TAG_COLOR } from '../../../lib/colors';
import { ProgramTagDataFragment } from '../../../../generated/graphql';

export type ProviderUserOption = {
  id: string;
  name?: string | null;
  email: string;
  isExternalCollaborator: boolean;
  profileImageMedia?: { url: string } | null;
};

// Form data structure for react-hook-form
export type FilterFormData = {
  providerUserIds: string[];
  tagIds: string[];
  excludedTagIds: string[];
  tagsMatch: 'ANY' | 'ALL';
  providersMatch: 'ANY' | 'ALL';
  filterOperator: 'AND' | 'OR';
  noAssignedPractitioners?: boolean;
  noTags?: boolean;
};

export type CombinedFilterProps = {
  availableProviderUsers: ProviderUserOption[];
  availableTags: ProgramTagDataFragment[];
  selectedProviderUserIds: string[];
  selectedTagIds: string[];
  selectedExcludedTagIds?: string[];
  selectedTagsMatch?: 'ANY' | 'ALL';
  selectedProvidersMatch?: 'ANY' | 'ALL';
  selectedFilterOperator?: 'AND' | 'OR';
  selectedNoAssignedPractitioners?: boolean;
  selectedNoTags?: boolean;
  onApply: (selection: FilterFormData) => void;
  onClear: () => void;
  disabled?: boolean;
};

const CombinedFilter: React.FC<CombinedFilterProps> = ({
  availableProviderUsers,
  availableTags,
  selectedProviderUserIds,
  selectedTagIds,
  selectedExcludedTagIds = [],
  selectedTagsMatch = 'ANY',
  selectedProvidersMatch = 'ANY',
  selectedFilterOperator = 'AND',
  selectedNoAssignedPractitioners = false,
  selectedNoTags = false,
  onApply,
  onClear,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const appliedViaButtonRef = useRef(false);
  const [practitionerSearch, setPractitionerSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [excludeTagSearch, setExcludeTagSearch] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Use react-hook-form for state management
  const methods = useForm<FilterFormData>({
    defaultValues: {
      providerUserIds: selectedProviderUserIds,
      tagIds: selectedTagIds,
      excludedTagIds: selectedExcludedTagIds,
      tagsMatch: selectedTagsMatch,
      providersMatch: selectedProvidersMatch,
      filterOperator: selectedFilterOperator,
    },
  });

  const { watch, setValue, reset } = methods;

  // Watch form values for real-time updates
  const watchedValues = watch();

  // Memoize filtered results for better performance
  const filteredPractitioners = useMemo(() => {
    if (!practitionerSearch.trim()) return availableProviderUsers;
    return availableProviderUsers.filter((user) =>
      (user.name || user.email)
        .toLowerCase()
        .includes(practitionerSearch.toLowerCase()),
    );
  }, [availableProviderUsers, practitionerSearch]);

  const filteredTags = useMemo(() => {
    if (!tagSearch.trim()) return availableTags;
    return availableTags.filter((tag) =>
      tag.name.toLowerCase().includes(tagSearch.toLowerCase()),
    );
  }, [availableTags, tagSearch]);

  const filteredExcludeTags = useMemo(() => {
    if (!excludeTagSearch.trim()) return availableTags;
    return availableTags.filter((tag) =>
      tag.name.toLowerCase().includes(excludeTagSearch.toLowerCase()),
    );
  }, [availableTags, excludeTagSearch]);

  // Track if "no assigned" and "no tag" are explicitly selected
  const [explicitlyNoAssigned, setExplicitlyNoAssigned] = useState(
    selectedNoAssignedPractitioners,
  );
  const [explicitlyNoTag, setExplicitlyNoTag] = useState(selectedNoTags);

  // Derived toggles for special options - only show when explicitly selected
  const noAssignedSelected = useMemo(() => {
    if (availableProviderUsers.length === 0) return false;
    if (watchedValues.providerUserIds.length !== 0) return false;
    return explicitlyNoAssigned;
  }, [
    availableProviderUsers,
    watchedValues.providerUserIds,
    explicitlyNoAssigned,
  ]);

  const noTagSelected = useMemo(() => {
    if (availableTags.length === 0) return false;
    if (watchedValues.tagIds.length !== 0) return false;
    return explicitlyNoTag;
  }, [availableTags, watchedValues.tagIds, explicitlyNoTag]);

  // Reset form when props change
  useEffect(() => {
    reset({
      providerUserIds: selectedProviderUserIds,
      tagIds: selectedTagIds,
      excludedTagIds: selectedExcludedTagIds,
      tagsMatch: selectedTagsMatch,
      providersMatch: selectedProvidersMatch,
      filterOperator: selectedFilterOperator,
    });
    // Reset explicit "no" selections to match original state
    setExplicitlyNoAssigned(selectedNoAssignedPractitioners);
    setExplicitlyNoTag(selectedNoTags);
  }, [
    selectedProviderUserIds,
    selectedTagIds,
    selectedExcludedTagIds,
    selectedTagsMatch,
    selectedProvidersMatch,
    selectedFilterOperator,
    selectedNoAssignedPractitioners,
    selectedNoTags,
    reset,
  ]);

  const providerFilterCount = noAssignedSelected
    ? 1
    : watchedValues.providerUserIds?.length || 0;
  const tagFilterCount = noTagSelected ? 1 : watchedValues.tagIds?.length || 0;
  const excludedTagFilterCount = watchedValues.excludedTagIds?.length || 0;
  const filterCount =
    providerFilterCount + tagFilterCount + excludedTagFilterCount;

  // Handlers extracted for clarity and to satisfy lint rules
  const handleRemoveIncludedUserChip = (id: string) => {
    const currentIds = watchedValues.providerUserIds.filter((x) => x !== id);
    setValue('providerUserIds', currentIds);
  };

  const handleRemoveIncludedTagChip = (id: string) => {
    const currentIds = watchedValues.tagIds.filter((x) => x !== id);
    setValue('tagIds', currentIds);
  };

  const toggleProviderUser = (id: string) => {
    const currentIds = watchedValues.providerUserIds;
    const newIds = currentIds.includes(id)
      ? currentIds.filter((x) => x !== id)
      : [...currentIds, id];
    setValue('providerUserIds', newIds);
  };

  const toggleIncludeTag = (id: string) => {
    const currentIds = watchedValues.tagIds;
    const newIds = currentIds.includes(id)
      ? currentIds.filter((x) => x !== id)
      : [...currentIds, id];
    setValue('tagIds', newIds);
    if (!currentIds.includes(id)) {
      setValue(
        'excludedTagIds',
        (watchedValues.excludedTagIds || []).filter((x) => x !== id),
      );
    }
  };

  const toggleExcludeTag = (id: string) => {
    const currentIds = watchedValues.excludedTagIds || [];
    const newIds = currentIds.includes(id)
      ? currentIds.filter((x) => x !== id)
      : [...currentIds, id];
    setValue('excludedTagIds', newIds);
    if (!currentIds.includes(id)) {
      setValue(
        'tagIds',
        watchedValues.tagIds.filter((x) => x !== id),
      );
      setExplicitlyNoTag(false);
    }
  };

  const handleRemoveExcludedTagChip = (id: string) => {
    setValue(
      'excludedTagIds',
      (watchedValues.excludedTagIds || []).filter((x) => x !== id),
    );
  };

  const toggleNoAssigned = () => {
    if (explicitlyNoAssigned) {
      // Turn off "no assigned" selection
      setExplicitlyNoAssigned(false);
    } else {
      // Turn on "no assigned" selection
      setExplicitlyNoAssigned(true);
      setValue('providerUserIds', []); // Clear any selected practitioners
    }
  };

  const toggleNoTag = () => {
    if (explicitlyNoTag) {
      // Turn off "no tag" selection
      setExplicitlyNoTag(false);
    } else {
      // Turn on "no tag" selection
      setExplicitlyNoTag(true);
      setValue('tagIds', []); // Clear any selected tags
    }
  };

  const handleApply = () => {
    lastActionRef.current = 'apply';
    appliedViaButtonRef.current = true;

    // Create filter payload with explicit "no" states
    const filterPayload = {
      ...watchedValues,
      // Add explicit "no" flags for backend processing
      no_assigned_practitioners: explicitlyNoAssigned,
      no_tags: explicitlyNoTag,
    };

    onApply(filterPayload);
    setIsOpen(false);
  };

  // Track if we're cancelling to avoid auto-apply
  const [isCancelling, setIsCancelling] = useState(false);

  // Track the last action to distinguish between different ways of closing
  const lastActionRef = useRef<'apply' | 'cancel' | 'escape' | 'click-outside'>(
    'click-outside',
  );

  // Reset cancelling state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsCancelling(false);
    }
  }, [isOpen]);

  // Handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        lastActionRef.current = 'escape';
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  return (
    <FormProvider {...methods}>
      <div className="flex items-center">
        <Button
          title={filterCount > 0 ? `Filters (${filterCount})` : 'Filters'}
          IconComponent={FilterIcon}
          iconPosition="left"
          theme="secondary"
          size="small"
          onClick={() => setIsOpen(true)}
          disabled={disabled}
        />

        <ModalDialog
          isOpen={isOpen}
          setClosed={() => {
            // Clicking outside or pressing Escape acts like Cancel - restore previous values
            if (!isCancelling) {
              reset({
                providerUserIds: selectedProviderUserIds,
                tagIds: selectedTagIds,
                excludedTagIds: selectedExcludedTagIds,
                tagsMatch: selectedTagsMatch,
                providersMatch: selectedProvidersMatch,
                filterOperator: selectedFilterOperator,
              });
              // Reset explicit "no" selections to match original state
              setExplicitlyNoAssigned(selectedNoAssignedPractitioners);
              setExplicitlyNoTag(selectedNoTags);
            }
            setIsOpen(false);
          }}
          width="large"
          overflow="auto"
          fullHeight
          title="Filters"
          leftFooter={
            <div className="flex items-center gap-3">
              <Button
                title="Cancel"
                theme="secondary"
                size="medium"
                onClick={() => {
                  lastActionRef.current = 'cancel';
                  setIsCancelling(true);
                  // Restore original values
                  reset({
                    providerUserIds: selectedProviderUserIds,
                    tagIds: selectedTagIds,
                    excludedTagIds: selectedExcludedTagIds,
                    tagsMatch: selectedTagsMatch,
                    providersMatch: selectedProvidersMatch,
                    filterOperator: selectedFilterOperator,
                  });
                  // Reset explicit "no" selections to match original state
                  setExplicitlyNoAssigned(selectedNoAssignedPractitioners);
                  setExplicitlyNoTag(selectedNoTags);
                  setIsOpen(false);
                }}
              />
            </div>
          }
          primaryActionTitle="Apply"
          primaryActionOnClick={handleApply}
          bodyClassName="flex flex-col min-h-0 pt-4"
        >
          <div className="flex min-h-0 flex-1 flex-col space-y-4 px-3">
            {/* Assigned Practitioners Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-body font-bold text-green-150">
                  Show clients assigned to{' '}
                  <button
                    className={classNames(
                      'rounded-full px-3 py-1.5 font-normal',
                      watchedValues.providersMatch === 'ANY'
                        ? 'bg-secondary-20 text-secondary-150'
                        : 'text-neutral-125',
                    )}
                    onClick={() => setValue('providersMatch', 'ANY')}
                  >
                    ANY
                  </button>{' '}
                  /{' '}
                  <button
                    className={classNames(
                      'rounded-full px-3 py-1.5 font-normal',
                      watchedValues.providersMatch === 'ALL'
                        ? 'bg-secondary-20 text-secondary-150'
                        : 'text-neutral-125',
                    )}
                    onClick={() => setValue('providersMatch', 'ALL')}
                  >
                    ALL
                  </button>{' '}
                  of these practitioners
                </div>
                {(noAssignedSelected ||
                  watchedValues.providerUserIds.length > 0) && (
                  <button
                    className="flex items-center gap-1 rounded-full bg-red-25 px-3 py-1.5 font-normal text-neutral-150 hover:bg-red-50/60"
                    onClick={() => {
                      setValue('providerUserIds', []);
                      setExplicitlyNoAssigned(false);
                    }}
                  >
                    <XIcon className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>

              {/* Practitioner Search Combobox */}
              <Combobox
                as="div"
                className="relative w-full text-body"
                value={null}
                onChange={(user: any) => {
                  if (user) {
                    if (user === 'no-assigned') {
                      toggleNoAssigned();
                    } else {
                      toggleProviderUser(user.id);
                    }
                  }
                }}
                immediate
              >
                <div className="relative">
                  <Combobox.Input
                    className={classNames(
                      'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
                      'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-secondary-150 shadow-sm sm:text-caption',
                      'border-0 placeholder:text-neutral-110 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
                      'selection:bg-secondary-20',
                    )}
                    onChange={(event) =>
                      setPractitionerSearch(event.target.value)
                    }
                    placeholder="Search practitioners..."
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-neutral-125"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setPractitionerSearch('')}
                >
                  <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                    <Combobox.Option key="no-assigned" value="no-assigned">
                      {({ active, selected }) => (
                        <div
                          className={classNames(
                            'relative flex cursor-pointer select-none flex-row items-center justify-between py-3 px-5 text-green-150 hover:bg-neutral-25',
                            {
                              'bg-neutral-25': selected || active,
                            },
                          )}
                        >
                          <div className="flex min-w-0 flex-1 items-center">
                            <div className="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-50 text-small-label text-neutral-125">
                              —
                            </div>
                            <span className="text-sm font-medium text-neutral-150">
                              No assigned team member
                            </span>
                          </div>
                        </div>
                      )}
                    </Combobox.Option>
                    {filteredPractitioners.map((user) => (
                      <Combobox.Option key={user.id} value={user}>
                        {({ active, selected }) => (
                          <div
                            className={classNames(
                              'relative flex cursor-pointer select-none flex-row items-center py-3 px-5 text-green-150 hover:bg-neutral-25',
                              {
                                'bg-neutral-25': selected || active,
                              },
                            )}
                          >
                            <div className="flex min-w-0 flex-1 items-center">
                              <Avatar
                                size="small"
                                name={user.name || user.email}
                                imageUrl={
                                  user.profileImageMedia?.url || undefined
                                }
                                className="mr-3"
                              />
                              <div className="flex min-w-0 flex-col">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm truncate font-medium text-neutral-150">
                                    {user.name || user.email}
                                  </span>
                                  {user.isExternalCollaborator && (
                                    <span className="inline-flex items-center rounded-full bg-neutral-75 px-1.5 py-0.5 text-small-label font-medium uppercase tracking-wide text-neutral-125">
                                      Collaborator
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                </Transition>
              </Combobox>

              {/* Selected Practitioner Filters */}
              <div
                className={classNames(
                  'flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-md p-2',
                  noAssignedSelected || watchedValues.providerUserIds.length > 0
                    ? 'min-h-[48px]'
                    : 'min-h-0',
                )}
              >
                {noAssignedSelected && (
                  <button
                    key="no-assigned-chip"
                    className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-50 px-3 py-1 text-small-caption ring-1 ring-neutral-100"
                    onClick={() => toggleNoAssigned()}
                  >
                    <span className="max-w-[200px] truncate text-neutral-150">
                      No assigned team member
                    </span>
                    <XIcon className="h-3 w-3 text-neutral-125" />
                  </button>
                )}
                {watchedValues.providerUserIds.map((id) => {
                  const u = availableProviderUsers.find((x) => x.id === id);
                  if (!u) return null;
                  return (
                    <button
                      key={`inc-u-${id}`}
                      className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-25 px-3 py-1 text-small-caption"
                      onClick={() => handleRemoveIncludedUserChip(id)}
                    >
                      <Avatar
                        imageUrl={u.profileImageMedia?.url || undefined}
                        name={u.name || u.email}
                        size="small"
                      />
                      <span className="max-w-[180px] truncate text-neutral-150">
                        {u.name || u.email}
                      </span>
                      <XIcon className="h-3 w-3 text-neutral-125" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AND/OR Toggle Button */}
            <div className="flex items-center">
              <div className="mr-4 h-px w-20 bg-neutral-75"></div>
              <div className="flex items-center gap-2 text-body font-medium text-neutral-125">
                <button
                  className={classNames(
                    'rounded-full px-3 py-1.5 font-normal',
                    watchedValues.filterOperator === 'AND'
                      ? 'bg-secondary-20 text-secondary-150'
                      : '',
                  )}
                  onClick={() => setValue('filterOperator', 'AND')}
                >
                  AND
                </button>{' '}
                <span className="font-bold">/</span>{' '}
                <button
                  className={classNames(
                    'rounded-full px-3 py-1.5 font-normal',
                    watchedValues.filterOperator === 'OR'
                      ? 'bg-secondary-20 text-secondary-150'
                      : '',
                  )}
                  onClick={() => setValue('filterOperator', 'OR')}
                >
                  OR
                </button>
              </div>
              <div className="ml-4 h-px flex-1 bg-neutral-75"></div>
            </div>

            {/* Tags Section */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <div className="text-body font-bold text-green-150">
                  Show clients tagged with{' '}
                  <button
                    className={classNames(
                      'rounded-full px-3 py-1.5 font-normal',
                      watchedValues.tagsMatch === 'ANY'
                        ? 'bg-secondary-20 text-secondary-150'
                        : 'text-neutral-125',
                    )}
                    onClick={() => setValue('tagsMatch', 'ANY')}
                  >
                    ANY
                  </button>{' '}
                  /{' '}
                  <button
                    className={classNames(
                      'rounded-full px-3 py-1.5 font-normal',
                      watchedValues.tagsMatch === 'ALL'
                        ? 'bg-secondary-20 text-secondary-150'
                        : 'text-neutral-125',
                    )}
                    onClick={() => setValue('tagsMatch', 'ALL')}
                  >
                    ALL
                  </button>{' '}
                  of these tags
                </div>
                {(noTagSelected || watchedValues.tagIds.length > 0) && (
                  <button
                    className="flex items-center gap-1 rounded-full bg-red-25 px-3 py-1.5 font-normal text-neutral-150 hover:bg-red-50/60"
                    onClick={() => {
                      setValue('tagIds', []);
                      setExplicitlyNoTag(false);
                    }}
                  >
                    <XIcon className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>

              {/* Tags Search Combobox */}
              <Combobox
                as="div"
                className="relative w-full text-body"
                value={null}
                onChange={(tag: any) => {
                  if (tag) {
                    if (tag === 'no-tag') {
                      toggleNoTag();
                    } else {
                      toggleIncludeTag(tag.id);
                    }
                  }
                }}
                immediate
              >
                <div className="relative">
                  <Combobox.Input
                    className={classNames(
                      'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
                      'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-secondary-150 shadow-sm sm:text-caption',
                      'border-0 placeholder:text-neutral-110 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
                      'selection:bg-secondary-20',
                    )}
                    onChange={(event) => setTagSearch(event.target.value)}
                    placeholder="Search tags..."
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-neutral-125"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setTagSearch('')}
                >
                  <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                    <Combobox.Option key="no-tag" value="no-tag">
                      {({ active, selected }) => (
                        <div
                          className={classNames(
                            'relative flex cursor-pointer select-none flex-row items-center justify-between py-3 px-5 text-green-150 hover:bg-neutral-25',
                            {
                              'bg-neutral-25': selected || active,
                            },
                          )}
                        >
                          <div className="flex min-w-0 flex-1 items-center">
                            <Tag className="mr-3 h-6 min-w-[24px] text-neutral-150" />
                            <span className="text-sm font-medium text-neutral-150">
                              No tag
                            </span>
                          </div>
                        </div>
                      )}
                    </Combobox.Option>
                    {filteredTags.map((tag) => (
                      <Combobox.Option key={tag.id} value={tag}>
                        {({ active, selected }) => (
                          <div
                            className={classNames(
                              'relative flex cursor-pointer select-none flex-row items-center py-3 px-5 text-green-150 hover:bg-neutral-25',
                              {
                                'bg-neutral-25': selected || active,
                              },
                            )}
                          >
                            <div className="flex min-w-0 flex-1 items-center">
                              <span
                                className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-caption"
                                style={getTagStyles(
                                  tag.color ?? DEFAULT_TAG_COLOR,
                                )}
                              >
                                <Tag className="h-6 min-w-[24px]" />
                                <span className="text-sm font-medium">
                                  {tag.name}
                                </span>
                              </span>
                            </div>
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                </Transition>
              </Combobox>

              {/* Selected Tag Filters */}
              <div
                className={classNames(
                  'flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-md p-2',
                  noTagSelected || watchedValues.tagIds.length > 0
                    ? 'min-h-[48px]'
                    : 'min-h-0',
                )}
              >
                {noTagSelected && (
                  <button
                    key="no-tag-chip"
                    className="inline-flex max-w-full items-center gap-2 rounded-full bg-neutral-50 px-3 py-1 text-small-caption ring-1 ring-neutral-100"
                    onClick={() => toggleNoTag()}
                  >
                    <Tag className="h-4 w-4 text-neutral-150" />
                    <span className="max-w-[200px] truncate text-neutral-150">
                      No tag
                    </span>
                    <XIcon className="h-3 w-3 text-neutral-125" />
                  </button>
                )}
                {watchedValues.tagIds.map((id) => {
                  const t = availableTags.find((x) => x.id === id);
                  if (!t) return null;
                  return (
                    <button
                      key={`inc-t-${id}`}
                      className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-small-caption"
                      style={getTagStyles(t.color ?? DEFAULT_TAG_COLOR)}
                      onClick={() => handleRemoveIncludedTagChip(id)}
                    >
                      <Tag className="h-4 w-4" />
                      <span className="max-w-[200px] truncate">{t.name}</span>
                      <XIcon className="h-3 w-3 text-neutral-125" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Hide Tags Section */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <div className="text-body font-bold text-green-150">
                  Hide clients tagged with
                </div>
                {(watchedValues.excludedTagIds?.length || 0) > 0 && (
                  <button
                    className="flex items-center gap-1 rounded-full bg-red-25 px-3 py-1.5 font-normal text-neutral-150 hover:bg-red-50/60"
                    onClick={() => {
                      setValue('excludedTagIds', []);
                    }}
                  >
                    <XIcon className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>

              <Combobox
                as="div"
                className="relative w-full text-body"
                value={null}
                onChange={(tag: ProgramTagDataFragment | null) => {
                  if (tag) {
                    toggleExcludeTag(tag.id);
                  }
                }}
                immediate
              >
                <div className="relative">
                  <Combobox.Input
                    className={classNames(
                      'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
                      'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-secondary-150 shadow-sm sm:text-caption',
                      'border-0 placeholder:text-neutral-110 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
                      'selection:bg-secondary-20',
                    )}
                    onChange={(event) =>
                      setExcludeTagSearch(event.target.value)
                    }
                    placeholder="Search tags to hide..."
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-neutral-125"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setExcludeTagSearch('')}
                >
                  <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                    {filteredExcludeTags.map((tag) => (
                      <Combobox.Option key={tag.id} value={tag}>
                        {({ active, selected }) => (
                          <div
                            className={classNames(
                              'relative flex cursor-pointer select-none flex-row items-center py-3 px-5 text-green-150 hover:bg-neutral-25',
                              {
                                'bg-neutral-25': selected || active,
                              },
                            )}
                          >
                            <div className="flex min-w-0 flex-1 items-center">
                              <span
                                className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-caption"
                                style={getTagStyles(
                                  tag.color ?? DEFAULT_TAG_COLOR,
                                )}
                              >
                                <Tag className="h-6 min-w-[24px]" />
                                <span className="text-sm font-medium">
                                  {tag.name}
                                </span>
                              </span>
                            </div>
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                </Transition>
              </Combobox>

              <div
                className={classNames(
                  'flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-md p-2',
                  (watchedValues.excludedTagIds?.length || 0) > 0
                    ? 'min-h-[48px]'
                    : 'min-h-0',
                )}
              >
                {(watchedValues.excludedTagIds || []).map((id) => {
                  const t = availableTags.find((x) => x.id === id);
                  if (!t) return null;
                  return (
                    <button
                      key={`exc-t-${id}`}
                      className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-small-caption"
                      style={getTagStyles(t.color ?? DEFAULT_TAG_COLOR)}
                      onClick={() => handleRemoveExcludedTagChip(id)}
                    >
                      <Tag className="h-4 w-4" />
                      <span className="max-w-[200px] truncate">{t.name}</span>
                      <XIcon className="h-3 w-3 text-neutral-125" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Help and Clear Buttons Row */}
            <div className="flex items-center justify-between pt-4">
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="group flex items-center gap-2 rounded p-1 text-green-150 transition-colors hover:bg-neutral-75 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-secondary-50"
                title="Filter help"
              >
                <QuestionMarkCircleIcon className="h-6 w-6 text-secondary-100" />
                <span className="text-sm font-medium">Help with filters</span>
              </button>
            </div>

            {/* Expandable Help Content at Bottom */}
            {showHelp && (
              <div className="rounded-lg p-3 text-caption">
                <div>
                  <ul className="mt-1 space-y-1">
                    <li>
                      <span className="text-caption">•</span>{' '}
                      <strong>
                        Search and select assigned practitioners or tags
                      </strong>{' '}
                      to include them in your filter
                    </li>
                    <li>
                      <span className="text-caption">•</span>{' '}
                      <strong>Use ANY</strong> to view clients assigned to any
                      selected practitioners or tagged with any selected tags,
                    </li>
                    <li>
                      <span className="text-caption">•</span>{' '}
                      <strong>Use ALL</strong> to view clients assigned to all
                      selected practitioners or tagged with all selected tags
                    </li>
                    <li>
                      <span className="text-caption">•</span>{' '}
                      <strong>Use AND / OR</strong> to combine assigned
                      practitioner and tag filters
                    </li>
                    <li className="ml-4">
                      <span className="text-caption">•</span>{' '}
                      <strong>Only matters when you have both</strong>{' '}
                      practitioner and tag filters selected, if you only filter
                      by one type, it won't affect your results
                    </li>
                    <li>
                      <span className="text-caption">•</span> Select{' '}
                      <strong>No assigned team member</strong> or{' '}
                      <strong>No tag</strong> to filter for clients without
                      assigned practitioners or tags
                    </li>
                    <li>
                      <span className="text-caption">•</span>{' '}
                      <strong>Hide clients tagged with</strong> removes anyone
                      who has any of those tags, including people who also match
                      the include set
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </ModalDialog>
      </div>
    </FormProvider>
  );
};

export default CombinedFilter;
